import { Link } from "react-router-dom";
import type { GossipEvent } from "@/mocks/gossip";

type EventCardProps = {
  event: GossipEvent;
  variant?: "default" | "wide" | "compact";
};

const levelTone: Record<string, string> = {
  primary: "bg-primary-100 text-primary-800",
  accent: "bg-accent-100 text-accent-800",
  secondary: "bg-secondary-100 text-secondary-800",
};

export default function EventCard({ event, variant = "default" }: EventCardProps) {
  const to = `/event/${event.id}`;

  if (variant === "compact") {
    return (
      <Link
        to={to}
        className="group flex items-start gap-4 rounded-lg border border-background-200 bg-background-100 p-3 hover:border-primary-300 hover:bg-background-50 transition-colors cursor-pointer"
      >
        <span className="w-24 h-20 shrink-0 overflow-hidden rounded-md bg-background-200">
          <img src={event.cover} alt={event.title} className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-500" />
        </span>
        <span className="min-w-0 flex flex-col">
          <span className="font-label text-[11px] text-primary-600">{event.category}</span>
          <span className="mt-1 font-heading font-semibold text-[15px] leading-snug text-foreground-950 line-clamp-2">
            {event.title}
          </span>
          <span className="mt-2 flex items-center gap-3 font-label text-[11px] text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-fire-line text-accent-600"></i>{event.heat}</span>
            <span className="flex items-center gap-1"><i className="ri-chat-3-line"></i>{event.comments}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "wide") {
    return (
      <Link
        to={to}
        className="group grid grid-cols-1 md:grid-cols-[320px_1fr] gap-0 overflow-hidden rounded-lg border border-background-200 bg-background-100 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="relative h-52 md:h-full overflow-hidden bg-background-200">
          <img src={event.cover} alt={event.title} className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-700" />
          <span className="absolute top-3 left-3 rounded-md bg-primary-500 px-2.5 py-1 font-label text-[11px] text-background-50">
            {event.category}
          </span>
        </div>
        <div className="p-5 md:p-7 flex flex-col justify-center">
          <h3 className="font-heading font-bold text-xl md:text-2xl leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors">
            {event.title}
          </h3>
          <p className="mt-2 font-label text-sm text-foreground-600 leading-relaxed line-clamp-2">
            {event.subtitle}
          </p>
          <p className="mt-3 font-label text-[13px] text-foreground-500 leading-relaxed line-clamp-2 hidden sm:block">
            {event.summary}
          </p>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-label text-xs text-foreground-500">
            <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{event.date}</span>
            <span className="flex items-center gap-1.5"><i className="ri-fire-line text-accent-600"></i>热度 {event.heat}</span>
            <span className="flex items-center gap-1.5"><i className="ri-chat-3-line"></i>{event.comments} 讨论</span>
            <span className="flex items-center gap-1.5"><i className="ri-time-line"></i>{event.readMin} 分钟读完</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={to}
      className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-100 hover:border-primary-300 transition-colors cursor-pointer"
    >
      <div className="relative h-48 overflow-hidden bg-background-200">
        <img src={event.cover} alt={event.title} className="w-full h-full object-top object-cover group-hover:scale-105 transition-transform duration-700" />
        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/45 to-transparent"></div>
        <span className="absolute top-3 left-3 rounded-md bg-primary-500 px-2.5 py-1 font-label text-[11px] text-background-50">
          {event.category}
        </span>
        <span className="absolute bottom-3 right-3 flex items-center gap-1 rounded-md bg-background-50/90 px-2 py-1 font-label text-[11px] text-foreground-800">
          <i className="ri-fire-line text-accent-600"></i>
          {event.heat}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="font-heading font-semibold text-base md:text-[17px] leading-snug text-foreground-950 group-hover:text-primary-700 transition-colors line-clamp-2">
          {event.title}
        </h3>
        <p className="mt-2 font-label text-[13px] leading-relaxed text-foreground-600 line-clamp-2 flex-1">
          {event.subtitle}
        </p>
        <div className="mt-4 flex flex-wrap gap-1.5">
          {event.tags.slice(0, 2).map((t) => (
            <span key={t} className="rounded-full bg-secondary-100 px-2.5 py-0.5 font-label text-[11px] text-secondary-800">
              {t}
            </span>
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between border-t border-background-200 pt-3 font-label text-[11px] text-foreground-500">
          <span className="flex items-center gap-1.5"><i className="ri-calendar-line"></i>{event.date}</span>
          <span className="flex items-center gap-1.5"><i className="ri-node-tree"></i>{event.timeline.length} 个节点</span>
        </div>
      </div>
    </Link>
  );
}

export const categoryTone = levelTone;